import { useRecoilTransaction_UNSTABLE } from "recoil"

import type { TransactionOperation } from "."
import { edgeIndex, findEdgeState } from "../edges"
import { nodeIndex } from "../nodes"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

export const surgicallyDeleteEdge: TransactionOperation<
  string,
  UnknownChangeForUnknownElement | undefined
> = (transactors, currentEdgeId) => {
  const { get, set } = transactors
  const edgeIds = get(edgeIndex)
  const edgeExists = edgeIds.includes(currentEdgeId)

  if (edgeExists) {
    set(edgeIndex, (edgeIds) =>
      edgeIds.filter((edgeId) => edgeId !== currentEdgeId),
    )

    const change = generateElementChange(transactors, {
      action: {
        type: `DELETE`,
        family: `EDGE`,
        id: currentEdgeId,
      },
    })

    return change
  }
  throw new Error(`Edge ${currentEdgeId} does not exist`)
}

// only delete one edge at a time
export const deleteEdge: TransactionOperation<
  string,
  (UnknownChangeForUnknownElement | undefined)[]
> = (transactors, currentEdgeId) => {
  const { get, set } = transactors
  const edgeIds = get(edgeIndex)
  const edgeExists = edgeIds.includes(currentEdgeId)

  if (edgeExists) {
    const { node1id, node2id } = get(findEdgeState(currentEdgeId))

    const tryToDeleteIsolatedNode = (currentNodeId: string) => {
      const nodeHasEdges = edgeIds.some(
        (edgeId) => edgeId !== currentEdgeId && edgeId.includes(currentNodeId),
      )
      if (!nodeHasEdges) {
        set(nodeIndex, (nodeIds) =>
          nodeIds.filter((nodeId) => currentNodeId !== nodeId),
        )

        return generateElementChange(transactors, {
          action: {
            type: `DELETE`,
            family: `NODE`,
            id: currentNodeId,
          },
        })
      }
    }

    const edgeDeletion = surgicallyDeleteEdge(transactors, currentEdgeId)

    const nodeDeletions = [node1id, node2id].map((id) =>
      tryToDeleteIsolatedNode(id),
    )

    return [edgeDeletion, ...nodeDeletions]
  }
  throw new Error(`Edge ${currentEdgeId} does not exist`)
}

export const eraseEdge: TransactionOperation<
  string,
  (UnknownChangeForUnknownElement | undefined)[]
> = (transactors, id) => {
  const changes = deleteEdge(transactors, id)
  divergeTimeline(transactors, changes)

  return changes
}

export const useEraseEdge = (id: string): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => eraseEdge(transactors, id),
  )
